import { storeToRefs } from 'pinia';
import dayjs from 'dayjs';
import { assign, isNil, snakeCase } from 'lodash-es';

import { TargetTagType } from '@/common/utils/types';
import { DB_TYPE } from '@/common/define/common.define';
import { ONE_SECOND } from '@/common/utils/define';

import { StatInfo } from '@/common/components/organisms/statWindow';
import { parsePeriod } from '@/common/utils/core/time/parse';
import { ChartDataStat } from '@/common/components/organisms/statIndicatorSelect/statIndicatorSelect.types';
import { SelectedTagData } from '@/common/components/templates/selectTargetTagWindow/selectTargetTagWindow.type';
import {
  CUSTOM_STAT_CATEGORY_PREFIX,
  CUSTOM_USER_INTEGRATION_CATEGORY_PREFIX,
} from '@/common/utils/statInfo.define';

import { useStatInfoStore } from '@/common/stores/stat-info';
import { useSeriesColorListStore } from '@/dashboard/stores/series-color-list';

import {
  AlertConfigRuleDetailV9,
  AlertConfigRuleDetailV9SubType,
  AlertRuleV9RequestCheckMode,
  ResponseEventCondition,
  ResponseMetricCondition,
  TagItem,
  WeeklyScheduleInfo,
} from '@/api-client/metav8Over/model';

import { ASTER_KEY } from '@/dashboard/features/widgets/common/constants/define';
import { WidgetTarget } from '@/dashboard/shared/types/widgets.types';
import { letterConverter } from '@/dashboard/features/widgets/common/utils/letterConverter.utils';
import {
  CommonRuleData,
  ConditionRowData,
  EventType,
  MetricSubset,
  ProcessDownPattern,
  ProcessDownVariableCondition,
  CheckInterval,
  WeeklySchedule,
  KubernetesVariableCondition,
  DatabaseVariableCondition,
} from '@/config/views/userAlert/alertRuleWindow.types';
import {
  ALERT_TYPE_VALUE,
  DEFAULT_TRANSPORT_SERVER_TYPE,
  EVENT_NAME_ENUM,
  EVENT_TYPE_ENUM,
  EVENT_TYPE_REQ_ENUM,
  EVENT_VARIABLE_CONDITION_ITEMS,
  getCheckHoldTimeItems,
  getEventTypeItems,
  getEventNameItems,
  getWeeklyScheduleItems,
  RULE_CRITERIA_ITEMS,
  FILE_CHANGE_MAP,
  FILE_CHANGE_REVERSE_MAP,
  CHECK_BY_TARGET,
  CHECK_BY_COMBINATION,
  DATABASE_SLOW_QUERY_THRESHOLD_DEFAULT_MS,
  ALERT_CONDITION_TYPE,
} from '@/alert/utils/define';
import { RuleCriteria } from '@/alert/components/alertDetail/alertDetail.types';
import { getProcessDownDefaultPattern } from '@/config/views/userAlert/alertRuleDataConditions/alertRuleDataConditionEventProcess.define';
import { getEventTypeKeys } from '@/config/views/userAlert/alertRuleDataConditions/alertRuleDataConditionEventServerChange.define';
import {
  IS_SEND_PER_USER_TYPES,
  USER_TEMPLATE_TOGGLE_TARGETS,
} from '@/config/components/alertNotification/alertNotification.define';
import {
  ALERT_CONTROL_NONE,
  ALERT_CONTROL_TYPE,
} from '@/config/views/userAlert/alertRuleDataCommon/alertRuleDataCommon.define';
import { NotificationItem, Notification } from '@/config/utils/alert.type';
import { isK8sTargetType } from '@/logs/utils/utils';
import { convertToDynamicTime, getFormattedTzTimeNow } from '@/common/utils/commonUtils';
import { DEFAULT_EXCLUDE_KEYWORD } from '@/common/components/molecules/excludeKeyword/excludeKeywordButton/excludeKeywordButton.define';
import { useExcludePatternButton } from '@/common/components/molecules/excludeKeyword/excludeKeywordButton/excludeKeywordButton.use';
import { useMyInfoStore } from '@/config/stores/my-info';

export const getEventName = (
  variableCondition?: ConditionRowData['event']['variableCondition'],
): string => {
  const eventName = variableCondition?.eventName?.[0] ?? '';
  if (FILE_CHANGE_REVERSE_MAP[eventName]) {
    return EVENT_NAME_ENUM.FILE;
  }
  return eventName;
};

export const getMetricSubset = (condition: { category?: string }): MetricSubset =>
  condition.category === 'process' ? 'process' : 'metric';

export const isK8sLoggingAlertCategory = ({ category }: { category?: string } = {}) =>
  isK8sTargetType(category ?? '');

// get init data
const getDefaultCheckInterval = (): CheckInterval => ({
  interval: 5,
  unit: 's',
});

const defaultDayWeek = getWeeklyScheduleItems().map((item) => `${item.value}`);
const getDefaultWeeklySchedule = () => {
  const { timezone } = storeToRefs(useMyInfoStore());
  return {
    dayWeek: defaultDayWeek,
    range: ['00:00', '23:59'],
    timeZone: timezone.value,
  };
};
export const getInitCommonRuleData = (): CommonRuleData => {
  return {
    type: ALERT_TYPE_VALUE.METRIC,
    ruleCriteria: RULE_CRITERIA_ITEMS[0].value,
    alertConditionType: ALERT_CONDITION_TYPE.SINGLE,
    multiAlertLevel: 'critical',
    checkInterval: getDefaultCheckInterval(),
    checkRepeat: 1,
    checkMode: AlertRuleV9RequestCheckMode.REPEATED_SAME_LEVEL,
    maximumOccurrences: '-1',
    alertMode: ALERT_CONTROL_TYPE.ALERT_CLEAR,
    checkHoldTime: ALERT_CONTROL_NONE,
    weeklySchedule: getDefaultWeeklySchedule(),
  };
};

const getFileInitVariableCondition = () => ({
  fileType: ['fileSize'],
  fileSizeComparison: ['>'],
  fileSizeValue: [''],
  fileSizeUnit: ['Byte'],
  fileNoChangeThreshold: [''],
  fileNoChangeUnit: ['m'],
  filePath: [''],
  hashFunction: ['CRC32'],
});

const getLoggingInitVariableCondition = () => ({
  loggingId: [],
  loggingName: [], // 수정 api 에서만 응답
  pathId: [],
});

const getKubernetesInitVariableCondition = (): KubernetesVariableCondition => ({
  resourceStatusType: [EVENT_VARIABLE_CONDITION_ITEMS.resourceStatusType[0].value],
  kind: [] as string[],
  eventId: [] as string[], // reason 에서 쓰이는 듯
  status: [] as string[], // phase status
});

const getProcessDownInitVariableCondition = (): ProcessDownVariableCondition => ({
  detectedPattern: [getProcessDownDefaultPattern()],
  excludePattern: [],
});

// 프로세스 수 변동 감지 임계치 기본값 (부등호 + Warning/Critical, unit: count)
const getProcessCountChangeInitVariableCondition = () => ({
  comparison: '>',
  warningValue: '',
  criticalValue: '',
});

const getEventNameInitVariableCondition = (eventType?: EventType, eventName?: string) => {
  if (eventName) {
    return { eventName: [eventName] };
  }

  if (eventType && getEventNameItems(eventType)?.length) {
    return { eventName: [getEventNameItems(eventType)[0].value] };
  }
  return {
    eventName: [],
  };
};

const getDatabaseInitVariableCondition = (eventType?: EventType, eventName?: string) => {
  if (eventType !== EVENT_TYPE_ENUM.DATABASE) return {};
  const name = eventName ?? getEventNameItems(eventType)?.[0]?.value;
  const initKind = name === EVENT_NAME_ENUM.DATABASE_SLOW_QUERY ? [DB_TYPE.POSTGRESQL] : [];
  return {
    kind: initKind,
    threshold: [`${DATABASE_SLOW_QUERY_THRESHOLD_DEFAULT_MS}`],
  };
};

export const getServerChangeInitVariableCondition = () => {
  return Object.values(getEventTypeKeys()).reduce((acc, item) => {
    if (!acc[item.key]) {
      acc[item.key] = [];
    }
    return acc;
  }, {});
};

export const getInitVariableCondition = ({
  eventType,
  eventName,
}: {
  eventType?: EventType;
  eventName?: string;
} = {}) =>
  ({
    facility: [EVENT_VARIABLE_CONDITION_ITEMS.facility[0].value],
    severity: [EVENT_VARIABLE_CONDITION_ITEMS.severity[0].value],
    genericTrapType: [EVENT_VARIABLE_CONDITION_ITEMS.genericTrapType[0].value],
    specificTrapCode: [] as string[],
    enterpriseOid: [] as string[],
    ...getLoggingInitVariableCondition(),
    ...getKubernetesInitVariableCondition(),
    ...getDatabaseInitVariableCondition(eventType, eventName),
    ...getProcessDownInitVariableCondition(),
    ...getProcessCountChangeInitVariableCondition(),
    ...getEventNameInitVariableCondition(eventType, eventName),
    ...getFileInitVariableCondition(),
    ...getServerChangeInitVariableCondition(),
    // TODO: 현재 모순된 모델이 섞여서 ts가 정상 동작 안함, 하위 설정인 variableCondition를 한번에 넣어서 처리하는 방식이 아닌 이벤트 타입별로 모델 분리 필요함
  }) as unknown as ConditionRowData['event']['variableCondition'];

export const getInitConditionRowData = {
  metric: (
    data: { seriesColor?: string; conditionRefId?: string; evaluateValue?: string } = {},
  ): ConditionRowData['metric'] => ({
    isVisible: true,
    conditionId: undefined,
    conditionRefId: data.conditionRefId,
    conditionType: 'data',
    statInfo: undefined,
    category: '',
    targets: [],
    aggregation: 'sum',
    critical: undefined,
    criticalComparison: '>',
    warning: undefined,
    warningComparison: '',
    evaluateValue: data.evaluateValue ?? 'avg',
    duringTimeInterval: 'p5m',
    unit: '',
    processName: '', // only process
    seriesColor: data.seriesColor,
    excludeKeyword: DEFAULT_EXCLUDE_KEYWORD,
  }),
  event: (
    data: {
      statInfo?: ChartDataStat;
      targets?: SelectedTagData[];
      eventType?: EventType;
      eventName?: string;
    } = {},
  ): ConditionRowData['event'] => ({
    statInfo: data?.statInfo,
    eventType: data?.eventType ?? getEventTypeItems()[0].value,
    level: null,
    messageText: '',
    targets: data?.targets ?? [],
    postPeriod: 'p5m',
    duringTimeInterval: 'p5m',
    variableCondition: getInitVariableCondition({
      eventType: data?.eventType,
      eventName: data?.eventName,
    }),
    excludeKeyword: DEFAULT_EXCLUDE_KEYWORD,
  }),
  table: (): ConditionRowData['table'] => {
    return {
      isVisible: true,
      statInfo: undefined,
      category: '',
      targets: [],
      aggregation: 'sum',
      critical: undefined,
      criticalComparison: '>',
      warning: undefined,
      warningComparison: '',
      evaluateValue: 'current',
      duringTimeInterval: 'p5m',
      unit: '',
      checkField: undefined,
      checkFieldDisplayName: undefined,
      alertFilter: {
        operator: 'AND',
        items: [],
      },
      displayColumns: [],
      excludeKeyword: DEFAULT_EXCLUDE_KEYWORD,
    };
  },
  formula: (data: { seriesColor?: string } = {}): ConditionRowData['formula'] => ({
    isVisible: true,
    conditionId: undefined,
    conditionType: 'formula',
    formulaAlias: '',
    formula: '',
    critical: undefined,
    criticalComparison: '>',
    warning: undefined,
    warningComparison: '',
    seriesColor: data.seriesColor,
  }),
  eventStatistics: (
    data: {
      statInfo?: ChartDataStat;
      targets?: SelectedTagData[];
      eventType?: EventType;
      eventName?: string;
      kind?: string;
    } = {},
  ): ConditionRowData['eventStatistics'] => ({
    statInfo: data?.statInfo,
    eventType: data?.eventType ?? EVENT_TYPE_ENUM.LOGGING,
    level: null,
    messageText: '',
    targets: data?.targets ?? [],
    postPeriod: 'p3d',
    duringTimeInterval: 'p5m',
    variableCondition: getInitVariableCondition({
      eventType: data?.eventType,
      eventName: data?.eventName,
    }),
  }),
};

export const writeContent = {
  title: '',
  content: '',
  fixedContent: '',
};

export const getInitNotificationItem = (type: string): NotificationItem => {
  if (['email', 'sms', 'kakaoTalk'].includes(type)) {
    return {
      ids: [],
      writeContent: { ...writeContent },
      transportServerType: DEFAULT_TRANSPORT_SERVER_TYPE[type],
      ...(type === 'sms' ? { smsType: 'SMS' } : {}),
    };
  }

  return {
    ids: [],
    writeContent: { ...writeContent },
    ...(IS_SEND_PER_USER_TYPES.includes(type) ? { combineUser: false } : {}),
    ...(USER_TEMPLATE_TOGGLE_TARGETS.includes(type) ? { sendUser: false } : {}),
  };
};
export const getInitNotification = (list: string[]): Notification => {
  if (!list?.length) {
    return {};
  }

  const tempObj: Notification = {};
  list.forEach((v) => {
    tempObj[v] = getInitNotificationItem(v);
  });
  tempObj.hasResolvedAlert = false;

  return tempObj;
};

const getTime = {
  forApi: (time: string): string => {
    return getFormattedTzTimeNow(`YYYY-MM-DD ${time}:00`);
  },
  forUi: (time: string): string => {
    return dayjs(time).format('HH:mm');
  },
};

export const convertCheckInterval = {
  forApi: (
    data: CheckInterval,
  ): { checkInterval: number; checkIntervalDisplayUnit: CheckInterval['unit'] } => {
    const period = `${data.interval}${data.unit}`;
    // NOTE: interval 값은 sec 단위로 저장 / 사용자 선택 단위 저장하여 화면에서 변환
    return {
      checkInterval: parsePeriod(period).offset,
      checkIntervalDisplayUnit: data.unit,
    };
  },
  forUi: (data: number, displayUnit: CheckInterval['unit'] = 's'): CheckInterval => {
    // NOTE: sec 단위로 저장한 interval 값을 사용자 선택 단위로 변경
    const { value, unit } = convertToDynamicTime(data * ONE_SECOND, {
      unitList: [displayUnit],
      valueType: 'number',
    });
    return {
      interval: +value,
      unit: unit as CheckInterval['unit'],
    };
  },
};

export const convertWeeklySchedule = {
  forApi: (data: WeeklySchedule): WeeklyScheduleInfo => ({
    dayWeek: data.dayWeek.join(','),
    startTime: getTime.forApi(data.range[0]),
    endTime: getTime.forApi(data.range[1]),
    timeZone: data.timeZone ?? '',
  }),
  forUi: (data: WeeklyScheduleInfo, timeZone?: string): WeeklySchedule => {
    const defaultSchedule = getDefaultWeeklySchedule();
    if (!data) {
      return defaultSchedule;
    }

    const setDataRange = () => {
      if (data.startTime && data.endTime) {
        return [getTime.forUi(data.startTime!), getTime.forUi(data.endTime!)];
      }
      if (data.startTime) {
        return [getTime.forUi(data.startTime!), ''];
      }

      return defaultSchedule.range;
    };
    return {
      dayWeek: data.dayWeek?.split(',').map((v) => v.trim()) ?? defaultSchedule.dayWeek,
      range: setDataRange(),
      timeZone: (data.timeZone || timeZone) ?? defaultSchedule.timeZone,
    };
  },
};

export const convertTarget = {
  forUi: (data: TagItem): SelectedTagData => ({
    groupTagId: data?.tagKey ?? '',
    tagId: data?.tagId ?? '',
    tagType: (data?.tagType as TargetTagType) ?? 'tag',
  }),
  forApi: (data: SelectedTagData): string => `${data.groupTagId};${data.tagType};${data.tagId}`,
  forMetricApi: (data: SelectedTagData): WidgetTarget => ({
    tagKey: data.groupTagId,
    tagType: data.tagType,
    tagValue: data.tagId,
  }),
};

export const convertStatInfo = {
  forUi: (category: string, dataId: string): ChartDataStat | undefined => {
    if (category && dataId) {
      return {
        childCategory: category,
        statId: dataId,
      };
    }
    return undefined;
  },
  forApi: (data: ChartDataStat): { category: string; dataId: string } => {
    if (data.parentCategory?.startsWith(CUSTOM_STAT_CATEGORY_PREFIX)) {
      return {
        category: data?.parentCategory,
        dataId: data?.statId,
      };
    }
    return {
      category: data?.childCategory,
      dataId: data?.statId,
    };
  },
};

export const parseProcessDownPattern = (patternString: string) => {
  const [_, category, dataType, pattern] = patternString.split(/^([^:]*):([^:]*):(.*)$/);
  return {
    category,
    dataType,
    pattern,
  };
};

const convertProcessDownVariable = {
  forUi: (patterns: string[]) => (patterns || []).map(parseProcessDownPattern),
  forApi: (patterns: ProcessDownPattern[]) =>
    (patterns || []).map(({ category, dataType, pattern }) =>
      Object.values({
        category,
        dataType,
        pattern: (pattern ?? '').trim(),
      }).join(':'),
    ),
};

export const convertVariableCondition = {
  forUi: (data, eventType) => {
    const variableCondition = assign(getInitVariableCondition(), data?.[0] ?? {});

    if (
      eventType === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION ||
      eventType === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION
    ) {
      const { detectedPattern, excludePattern } = variableCondition;
      return {
        ...variableCondition,
        detectedPattern: convertProcessDownVariable.forUi(detectedPattern),
        excludePattern: convertProcessDownVariable.forUi(excludePattern),
        eventName: [eventType],
      };
    }

    if (
      eventType === EVENT_NAME_ENUM.NETWORK_SYSLOG ||
      eventType === EVENT_NAME_ENUM.NETWORK_TRAP
    ) {
      return {
        ...variableCondition,
        eventName: [eventType],
      };
    }

    if (
      eventType === EVENT_TYPE_ENUM.HOST &&
      FILE_CHANGE_REVERSE_MAP[variableCondition.eventName?.[0]]
    ) {
      return {
        ...variableCondition,
        eventName: [EVENT_NAME_ENUM.FILE],
        fileType: [FILE_CHANGE_REVERSE_MAP[variableCondition.eventName?.[0]]],
      };
    }

    return variableCondition;
  },
  forApi: (data, eventType) => {
    if (eventType === EVENT_TYPE_ENUM.LOGGING) {
      const { pathId, loggingId } = data;
      return [{ pathId, loggingId }];
    }

    if (eventType === EVENT_TYPE_ENUM.KUBERNETES) {
      const { kind, eventId, status, resourceStatusType } = data as KubernetesVariableCondition;
      return [{ kind, eventId, status, resourceStatusType }];
    }

    if (eventType === EVENT_TYPE_ENUM.APPLICATION) {
      const { eventName } = data;
      return [{ eventName }];
    }

    if (eventType === EVENT_TYPE_ENUM.DATABASE) {
      const { kind, eventName, threshold } = data as DatabaseVariableCondition;
      return [{ kind, eventName, threshold }];
    }

    const eventName = data?.eventName?.[0];

    if (eventType === EVENT_TYPE_ENUM.NETWORK && eventName === EVENT_NAME_ENUM.NETWORK_SYSLOG) {
      const { facility, severity } = data;
      return [{ facility, severity }];
    }

    if (eventType === EVENT_TYPE_ENUM.HOST) {
      if (
        eventName === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION ||
        eventName === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION
      ) {
        const { detectedPattern, excludePattern } = data;
        return [
          {
            detectedPattern: convertProcessDownVariable.forApi(detectedPattern),
            excludePattern: convertProcessDownVariable.forApi(excludePattern),
          },
        ];
      }
      if (eventName === EVENT_NAME_ENUM.FILE) {
        const {
          fileType,
          fileSizeComparison,
          fileSizeValue,
          fileSizeUnit,
          fileNoChangeThreshold,
          fileNoChangeUnit,
          filePath,
          hashFunction,
        } = data;

        switch (fileType[0]) {
          case 'fileSize':
            return [
              {
                eventName: [FILE_CHANGE_MAP.fileSize],
                fileSizeComparison,
                fileSizeValue,
                fileSizeUnit,
                filePath,
              },
            ];
          case 'fileChange':
            return [{ eventName: [FILE_CHANGE_MAP.fileChange], filePath, hashFunction }];
          case 'fileNoChange':
            return [
              {
                eventName: [FILE_CHANGE_MAP.fileNoChange],
                fileNoChangeThreshold,
                fileNoChangeUnit,
                filePath,
                hashFunction,
              },
            ];
          default:
            return [];
        }
      }

      if (eventName === EVENT_NAME_ENUM.SERVER_CHANGE) {
        const conditionData = { eventName: [eventName] };
        const keys = Object.values(getEventTypeKeys()).map((item) => item.key);
        keys.forEach((key) => {
          if (data[key]) {
            conditionData[key] = data[key];
          }
        });
        return [conditionData];
      }
    }

    const { genericTrapType, specificTrapCode, enterpriseOid } = data;
    return [{ genericTrapType, specificTrapCode, enterpriseOid }];
  },
};

// 이벤트 타입 분류 변경에 따른 작업. 기존 구조대로 api 주고 받기 위함
export const convertEventType = {
  forUi: (eventType: string): EventType => {
    switch (eventType) {
      case EVENT_NAME_ENUM.NETWORK_SYSLOG:
      case EVENT_NAME_ENUM.NETWORK_TRAP:
        return EVENT_TYPE_ENUM.NETWORK;
      case EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION:
      case EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION:
        return EVENT_TYPE_ENUM.HOST;
      default:
        return eventType as EventType;
    }
  },
  forApi: ({
    eventType,
    eventName,
    resourceStatusType,
  }: {
    eventType: EventType;
    eventName: string;
    resourceStatusType?: string;
  }): string => {
    const targetEventNames = [
      EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION,
      EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION,
      EVENT_NAME_ENUM.NETWORK_SYSLOG,
      EVENT_NAME_ENUM.NETWORK_TRAP,
    ];
    if (targetEventNames.includes(eventName)) {
      return snakeCase(eventName).toUpperCase();
    }
    if (eventType === EVENT_TYPE_ENUM.KUBERNETES && resourceStatusType === 'Phase') {
      return EVENT_TYPE_REQ_ENUM['Kubernetes Phase'];
    }
    return EVENT_TYPE_REQ_ENUM[eventType];
  },
};

// 부모 카테고리에 따른 지표 카테고리 변경
export const getStatInfoCategoryByParentCategory = (
  originalCategory: string,
  statInfo?: StatInfo,
): string => {
  // Custom Stat 지표
  if (statInfo?.parentCategory?.startsWith(CUSTOM_STAT_CATEGORY_PREFIX)) {
    return statInfo.childCategory;
  }

  // User Integration 지표
  if (statInfo?.parentCategory?.startsWith(CUSTOM_USER_INTEGRATION_CATEGORY_PREFIX)) {
    return statInfo.parentCategory;
  }
  return originalCategory;
};

export const getHoldTimeName = (checkHoldTime: string) => {
  const found = getCheckHoldTimeItems().find((opt) => opt.value === checkHoldTime);
  return found?.name || checkHoldTime;
};

export const getAlertRuleWindowData = () => {
  const filterDeletedTag = (item: TagItem) => item.tagId === ASTER_KEY || !isNil(item.tagName);

  const { getDefaultSeriesColors } = useSeriesColorListStore();
  const seriesColorList = getDefaultSeriesColors();
  const { extractExcludeKeywords } = useExcludePatternButton();

  // 공통 규칙 데이터 생성 함수
  const createCommonRuleData = (
    resData: AlertConfigRuleDetailV9,
    timeZone: string,
  ): CommonRuleData => {
    return {
      type: resData.type ?? ALERT_TYPE_VALUE.METRIC,
      ruleCriteria: (resData.ruleCriteria ?? CHECK_BY_TARGET) as RuleCriteria,
      checkInterval: convertCheckInterval.forUi(
        resData.checkInterval ?? 5,
        (resData.checkIntervalDisplayUnit ?? 's') as CheckInterval['unit'],
      ),
      checkRepeat: resData.checkRepeat ?? 3,
      checkMode: resData.checkMode ?? AlertRuleV9RequestCheckMode.REPEATED_SAME_LEVEL,
      maximumOccurrences: String(resData.maximumOccurrences ?? 0),
      checkHoldTime: resData.checkHoldTime ?? '-1',
      alertMode: resData.alertMode ?? 0,
      alertConditionType:
        resData.subType === AlertConfigRuleDetailV9SubType.CUSTOM
          ? ALERT_CONDITION_TYPE.CUSTOM
          : resData.subType === AlertConfigRuleDetailV9SubType.COMPOUND
            ? ALERT_CONDITION_TYPE.MULTI
            : ALERT_CONDITION_TYPE.SINGLE,
      multiAlertLevel: (() => {
        if (resData.subType === AlertConfigRuleDetailV9SubType.CUSTOM) {
          const formulaCondition = resData.metricConditions?.find(
            (c) => c.conditionType === 'formula',
          );
          return formulaCondition?.criticalComparison ? 'critical' : 'warning';
        }
        if (resData.subType === AlertConfigRuleDetailV9SubType.COMPOUND) {
          const dataCondition = resData.metricConditions?.find(
            (c) => c.conditionType !== 'formula',
          );
          return dataCondition?.criticalComparison ? 'critical' : 'warning';
        }
        return 'critical';
      })(),
      weeklySchedule: convertWeeklySchedule.forUi(resData.weeklySchedule!, timeZone),
    };
  };

  // 메트릭 조건 데이터 변환 함수
  const convertMetricConditions = (
    conditions: ResponseMetricCondition[],
    statInfoStore: ReturnType<typeof useStatInfoStore>,
  ) => {
    return conditions
      .filter((item) => item.conditionType !== 'formula')
      .map((item, index) => {
        delete item.formula;
        delete item.formulaAlias;
        const {
          aggregation,
          conditionId,
          conditionRefId,
          conditionType,
          category,
          critical,
          criticalComparison,
          dataId,
          duringTimeInterval,
          evaluateValue,
          targets,
          warning,
          warningComparison,
          activation,
          excludeTags,
          presetId,
          presetName,
        } = item;

        const findStat = statInfoStore.getStatInfo({ statId: dataId ?? '' });
        return {
          ...item,
          isVisible: activation,
          conditionType: conditionType ?? 'data',
          conditionId: conditionId ?? undefined,
          conditionRefId: isNil(conditionRefId)
            ? letterConverter.indexToAlphabet(index)
            : conditionRefId,
          aggregation,
          statInfo: convertStatInfo.forUi(
            getStatInfoCategoryByParentCategory(category || '', findStat),
            dataId || '',
          ),
          critical,
          criticalComparison,
          warning,
          warningComparison,
          duringTimeInterval: duringTimeInterval || 'p5m',
          evaluateValue,
          targets: targets
            ?.map(({ tagItems }) =>
              tagItems?.filter(filterDeletedTag).map((v) => convertTarget.forUi(v)),
            )
            .flat(),
          excludeKeyword: extractExcludeKeywords(excludeTags || ''),
          presetInfo:
            presetId && presetName
              ? {
                  id: presetId,
                  name: presetName,
                }
              : undefined,
        };
      });
  };

  // 이벤트 조건 데이터 변환 함수
  const convertEventConditions = (conditions: ResponseEventCondition[]) => {
    return conditions.map((item) => {
      const {
        category,
        dataId,
        eventType,
        level,
        messageText,
        targets,
        postPeriod,
        duringTimeInterval,
        variableConditionList,
        excludeTags,
      } = item;

      // 프로세스 수 변동 감지: condition 최상위 임계치를 UI variableCondition 스칼라로 환원
      const threshold = item as {
        comparison?: string;
        warningThreshold?: number;
        criticalThreshold?: number;
      };
      const hasThreshold =
        threshold.comparison != null ||
        threshold.warningThreshold != null ||
        threshold.criticalThreshold != null;
      const thresholdScalars = hasThreshold
        ? {
            comparison: threshold.comparison ?? '>',
            warningValue:
              threshold.warningThreshold == null ? '' : String(threshold.warningThreshold),
            criticalValue:
              threshold.criticalThreshold == null ? '' : String(threshold.criticalThreshold),
          }
        : {};

      return {
        isVisible: true,
        eventType: convertEventType.forUi(eventType ?? ''),
        level,
        messageText,
        statInfo: convertStatInfo.forUi(category ?? '', dataId ?? ''),
        targets: targets
          ?.map(({ tagItems }) =>
            tagItems?.filter(filterDeletedTag).map((v) => convertTarget.forUi(v)),
          )
          .flat(),
        postPeriod,
        duringTimeInterval,
        variableCondition: {
          ...convertVariableCondition.forUi(variableConditionList, eventType),
          ...thresholdScalars,
        },
        excludeKeyword: extractExcludeKeywords(excludeTags || ''),
      };
    });
  };

  // 포뮬라 조건 데이터 변환
  const convertFormulaConditions = (
    conditions: ResponseMetricCondition[],
    multiAlertLevel?: 'critical' | 'warning',
  ) => {
    return conditions
      .filter((item) => item.conditionType === 'formula')
      .map((item) => {
        const {
          conditionId,
          conditionType,
          critical,
          criticalComparison,
          warning,
          warningComparison,
          formula,
          formulaAlias,
          seriesColor,
          activation,
        } = item;

        // 기존 데이터 호환: warning 레벨인데 critical 필드에만 값이 있는 경우 마이그레이션
        const isWarningLevel = multiAlertLevel === 'warning';
        const migratedCritical = isWarningLevel ? null : critical;
        const migratedCriticalComparison = isWarningLevel ? '' : criticalComparison;
        const migratedWarning = isWarningLevel ? (warning ?? critical) : warning;
        const migratedWarningComparison = isWarningLevel
          ? warningComparison || criticalComparison
          : warningComparison;

        return {
          isVisible: activation,
          conditionId: conditionId ?? undefined,
          conditionType,
          formula,
          formulaAlias,
          critical: migratedCritical,
          criticalComparison: migratedCriticalComparison,
          warning: migratedWarning,
          warningComparison: migratedWarningComparison,
          seriesColor,
        };
      });
  };

  // NOTE: table 조건은 metric 조건과 동일한 형식으로 변환되서 일단 동일한 함수 사용
  const convertTableConditions = convertMetricConditions;
  // 조건 데이터 변환 함수
  const convertConditionsData = (
    resData: AlertConfigRuleDetailV9,
    statInfoStore: ReturnType<typeof useStatInfoStore>,
  ) => {
    if (resData.type === ALERT_TYPE_VALUE.METRIC) {
      const conditions = resData.metricConditions || [];
      const multiAlertLevel = (() => {
        if (resData.subType === AlertConfigRuleDetailV9SubType.CUSTOM) {
          const formulaCondition = resData.metricConditions?.find(
            (c) => c.conditionType === 'formula',
          );
          return formulaCondition?.criticalComparison ? 'critical' : 'warning';
        }
        if (resData.subType === AlertConfigRuleDetailV9SubType.COMPOUND) {
          const dataCondition = resData.metricConditions?.find(
            (c) => c.conditionType !== 'formula',
          );
          return dataCondition?.criticalComparison ? 'critical' : 'warning';
        }
        return undefined;
      })();

      return {
        metric: convertMetricConditions(conditions, statInfoStore),
        event: [getInitConditionRowData.event()],
        table: [getInitConditionRowData.table()],
        formula: convertFormulaConditions(
          conditions,
          multiAlertLevel as 'critical' | 'warning' | undefined,
        ),
        eventStatistics: [getInitConditionRowData.eventStatistics()],
      };
    }

    if (resData.type === ALERT_TYPE_VALUE.EVENT) {
      const conditions = resData.eventConditions || [];
      return {
        metric: [
          getInitConditionRowData.metric({ seriesColor: seriesColorList[0], conditionRefId: 'A' }),
        ],
        event: convertEventConditions(conditions),
        table: [getInitConditionRowData.table()],
        formula: [],
        eventStatistics: [getInitConditionRowData.eventStatistics()],
      };
    }

    if (resData.type === ALERT_TYPE_VALUE.TABLE) {
      const conditions = resData.metricConditions || [];
      return {
        metric: [
          getInitConditionRowData.metric({ seriesColor: seriesColorList[0], conditionRefId: 'A' }),
        ],
        event: [getInitConditionRowData.event()],
        table: convertTableConditions(conditions, statInfoStore),
        formula: [],
        eventStatistics: [getInitConditionRowData.eventStatistics()],
      };
    }
    if (resData.type === ALERT_TYPE_VALUE.EVENT_STATISTICS) {
      const conditions = resData.eventConditions || [];
      return {
        metric: [getInitConditionRowData.metric({ seriesColor: seriesColorList[0] })],
        event: [getInitConditionRowData.event()],
        table: [getInitConditionRowData.table()],
        eventStatistics: convertEventConditions(conditions),
      };
    }

    return {
      metric: [
        getInitConditionRowData.metric({ seriesColor: seriesColorList[0], conditionRefId: 'A' }),
      ],
      event: [getInitConditionRowData.event()],
      table: [getInitConditionRowData.table()],
      formula: [],
      eventStatistics: [getInitConditionRowData.eventStatistics()],
    };
  };

  return {
    createCommonRuleData,
    convertConditionsData,
  };
};

export const getAlertRuleWindowDataForApi = () => {
  const convertMetricConditionsForApi = ({
    conditions,
    statInfoStore,
    commonRuleData,
  }: {
    conditions: ConditionRowData['metric'][];
    statInfoStore: ReturnType<typeof useStatInfoStore>;
    commonRuleData: CommonRuleData;
  }) => {
    const { convertExcludeKeywordToParams } = useExcludePatternButton();

    return conditions.map((item, index) => {
      const storedStatInfo = statInfoStore.getStatInfo({ statId: item.statInfo?.statId || '' });
      const statInfo = convertStatInfo.forApi({
        ...item.statInfo!,
        parentCategory: storedStatInfo?.parentCategory,
      });

      const {
        statInfo: _statInfo,
        unit: _unit,
        isVisible: _isVisible,
        conditionId: _conditionId,
        excludeKeyword: _excludeKeyword,
        presetInfo: _presetInfo,
        ...rest
      } = item;

      const tags = rest.targets.map((v) => convertTarget.forApi(v));

      if (commonRuleData.ruleCriteria !== CHECK_BY_COMBINATION) {
        rest.aggregation = '';
      }

      if (rest.evaluateValue === 'current') {
        rest.duringTimeInterval = '';
      }

      const isCustomMode = commonRuleData.alertConditionType === ALERT_CONDITION_TYPE.CUSTOM;
      const isMultiMode = commonRuleData.alertConditionType === ALERT_CONDITION_TYPE.MULTI;
      const isCriticalLevel = commonRuleData.multiAlertLevel !== 'warning';

      return {
        ...rest,
        ...statInfo,
        activation: _isVisible,
        conditionId: _conditionId ?? null,
        conditionRefId: letterConverter.indexToAlphabet(index),
        tags,
        critical:
          isCustomMode || (isMultiMode && !isCriticalLevel) ? null : (rest.critical ?? null),
        criticalComparison:
          isCustomMode || (isMultiMode && !isCriticalLevel) ? '' : rest.criticalComparison,
        warning: isCustomMode || (isMultiMode && isCriticalLevel) ? null : (rest.warning ?? null),
        warningComparison:
          isCustomMode || (isMultiMode && isCriticalLevel) ? '' : rest.warningComparison,
        excludeTags: convertExcludeKeywordToParams(
          _excludeKeyword?.excludeKeywordSegmentedType,
          _excludeKeyword?.excludeKeywordList,
          item.category!,
        ),
        presetId: _presetInfo?.id,
        presetName: _presetInfo?.name,
      };
    });
  };

  const convertFormulaConditionsForApi = ({
    conditions,
    commonRuleData,
  }: {
    conditions: ConditionRowData['formula'][];
    commonRuleData: CommonRuleData;
  }) => {
    if (!conditions.length) {
      return [];
    }

    const isCriticalLevel = commonRuleData.multiAlertLevel !== 'warning';

    return conditions.map((item, index) => {
      const {
        isVisible: _isVisible,
        conditionId: _conditionId,
        critical,
        criticalComparison,
        warning,
        warningComparison,
        ...rest
      } = item ?? {};

      return {
        ...rest,
        conditionId: _conditionId ?? null,
        conditionRefId: `F${index + 1}`,
        activation: _isVisible,
        critical: !isCriticalLevel ? null : (critical ?? null),
        criticalComparison: !isCriticalLevel ? '' : criticalComparison,
        warning: isCriticalLevel ? null : (warning ?? null),
        warningComparison: isCriticalLevel ? '' : warningComparison,
      };
    });
  };

  return {
    convertMetricConditionsForApi,
    convertFormulaConditionsForApi,
  };
};
